<script>
import axios from "axios";
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet';
import { vendorMethods, vendorComputed } from "@/state/helpers";

export default {
  page: {
    title: "Service Provider",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, LMap, LTileLayer, LMarker, LPopup },
  data() {
    return {
      title: "Service Provider",
      shopDetail: null,
      shopProducts: [],
      items: [
        { text: "Ecommerce", href: "/" },
        { text: "Service Provider", active: true },
      ],
      vendor_id: '',
      loader: false,
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    };
  },
  mounted() {
    this.shopDetail = this.vendors.filter((product) => {
      return product.user_id == parseInt(this.$route.query.id);
    });
    this.vendor_id = this.shopDetail[0].user_id;
    this.fetchShopProducts();
  },
  computed: {
    ...vendorComputed,
  },
  methods: {
    ...vendorMethods,
    getFiles(name) {
      // if (!name) return require('@/assets/images/default-provider.jpg');
      let url = axios.defaults.baseURL;
      return (url).slice(0,-3) + "logos/" + name;
    },
    getProductFiles(name) {
      // if (!name) return require('@/assets/images/default-product.jpg');
      let url = axios.defaults.baseURL;
      return (url).slice(0,-3) + "products/" + name;
    },
    commas(x) { 
      return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0'; 
    },
    async fetchShopProducts() {
      this.loader = true;
      let formData = new FormData();
      formData.append("vendor_id", this.vendor_id);
      let url = "/venderproducts";
      await axios.post(url, formData).then((response) => {
        if (response.status == 200) {
          this.shopProducts = response.data.products;
        }
      }).finally(() => {
        this.loader = false;
      });
    },
  },
};
</script>

<template>
  <Layout>
    <div class="service-provider-container">
      <!-- Back Button -->
      <button @click="$router.go(-1)" class="back-button">
        <i class='mdi mdi-arrow-left'></i>
        <span>Back to Providers</span>
      </button>

      <!-- Provider Header Section -->
      <div class="provider-header">
        <div class="provider-info">
          <div class="logo-container">
            <img 
              :src="getFiles(shopDetail[0].logo)" 
              :alt="shopDetail[0].business_name"
              class="provider-logo"
            >
          </div>
          <div class="provider-details">
            <h1>{{ shopDetail[0].business_name }}</h1>
            <div class="contact-info">
              <div class="info-item">
                <i class="mdi mdi-map-marker-outline"></i>
                <span>{{ shopDetail[0].business_address }}</span>
              </div>
              <div class="info-item">
                <i class="mdi mdi-phone-outline"></i>
                <a :href="`tel:${shopDetail[0].business_contact}`">{{ shopDetail[0].business_contact }}</a>
              </div>
            </div>
          </div>
        </div>

        <!-- Map Section -->
        <div class="provider-map">
          <l-map 
            :zoom="13" 
            :center="[shopDetail[0].latitude, shopDetail[0].longitude]"
            class="map-container"
          >
            <l-tile-layer :url="url"></l-tile-layer>
            <l-marker :lat-lng="[shopDetail[0].latitude, shopDetail[0].longitude]">
              <l-popup>{{ shopDetail[0].business_name }}</l-popup>
            </l-marker>
          </l-map>
        </div>
      </div>

      <!-- Products/Services Section -->
      <section class="products-section">
        <h2>Services & Products</h2>
        
        <!-- Loading State -->
        <div v-if="loader" class="loading-grid">
          <div v-for="i in 6" :key="i" class="product-card-skeleton">
            <div class="image-skeleton"></div>
            <div class="content-skeleton">
              <div class="title-skeleton"></div>
              <div class="price-skeleton"></div>
            </div>
          </div>
        </div>

        <!-- Products Grid -->
        <div v-else class="products-grid">
          <router-link 
            v-for="product in shopProducts" 
            :key="product.id" 
            :to="{ path: '/product', query: { id: product.product_id }}"
            class="product-card"
          >
            <div class="product-image-container">
              <div class="category-badge">
                {{ product.category.name }}
              </div>
              <img 
                :src="getProductFiles(product.photo)" 
                :alt="product.name"
                class="product-image"
                loading="lazy"
              >
            </div>
            <div class="product-details">
              <h3>{{ product.name }}</h3>
              <div class="product-price">
                {{ commas(product.price) }} AED
              </div>
            </div>
          </router-link>
        </div>

        <!-- Empty State -->
        <div v-if="!loader && shopProducts.length === 0" class="empty-state">
          <i class="mdi mdi-package-variant"></i>
          <h3>No Services Available</h3>
          <p>This provider doesn't have any listed services yet</p>
        </div>
      </section>
    </div>
  </Layout>
</template>

<style scoped>
/* Color Variables */
:root {
  --primary: #564ab1;
  --primary-light: #6f65c2;
  --primary-dark: #453d8f;
  --secondary: #E6FDFF;
  --text-dark: #2d3748;
  --text-light: #718096;
  --white: #ffffff;
  --gray-light: #f7fafc;
  --gray: #e2e8f0;
  --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --transition: all 0.3s ease;
}

/* Base Container */
.service-provider-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

/* Back Button */
.back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--white);
  border: 1px solid var(--gray);
  color: var(--primary);
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: var(--transition);
  margin-bottom: 30px;
}

.back-button:hover {
  background: var(--gray-light);
  transform: translateX(-3px);
}

/* Provider Header */
.provider-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 40px;
}

.provider-info {
  display: flex;
  gap: 20px;
}

.logo-container {
  width: 150px;
  height: 150px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: var(--shadow);
}

.provider-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.provider-details {
  flex: 1;
}

.provider-details h1 {
  font-size: 28px;
  margin-bottom: 15px;
  color: var(--text-dark);
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-light);
  font-size: 16px;
}

.info-item a {
  color: var(--text-light);
  text-decoration: none;
  transition: var(--transition);
}

.info-item a:hover {
  color: var(--primary);
}

/* Map */
.provider-map {
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow);
}

.map-container {
  height: 100%;
  width: 100%;
}

/* Products Section */
.products-section {
  margin-top: 40px;
}

.products-section h2 {
  font-size: 24px;
  margin-bottom: 25px;
  color: var(--text-dark);
  position: relative;
  padding-bottom: 10px;
}

.products-section h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 3px;
  background: var(--primary);
}

/* Loading State */
.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.product-card-skeleton {
  background: var(--white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow);
}

.image-skeleton {
  height: 180px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.content-skeleton {
  padding: 15px;
}

.title-skeleton {
  height: 20px;
  width: 80%;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 12px;
}

.price-skeleton {
  height: 16px;
  width: 60%;
  background: #f0f0f0;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Products Grid */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.product-card {
  background: var(--white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: var(--transition);
  text-decoration: none;
  color: var(--text-dark);
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.product-image-container {
  position: relative;
  height: 180px;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: var(--transition);
}

.product-card:hover .product-image {
  transform: scale(1.05);
}

.category-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: var(--primary);
  color: var(--white);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  z-index: 1;
}

.product-details {
  padding: 15px;
}

.product-details h3 {
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-price {
  font-size: 16px;
  font-weight: 600;
  color: var(--primary);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: var(--white);
  border-radius: 12px;
  box-shadow: var(--shadow);
}

.empty-state i {
  font-size: 60px;
  color: var(--primary);
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 22px;
  margin-bottom: 10px;
  color: var(--text-dark);
}

.empty-state p {
  color: var(--text-light);
  font-size: 16px;
}

/* Responsive Design */
@media (max-width: 992px) {
  .provider-header {
    grid-template-columns: 1fr;
  }
  
  .provider-map {
    height: 300px;
  }
}

@media (max-width: 768px) {
  .provider-info {
    flex-direction: column;
  }
  
  .logo-container {
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
  }
  
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}

@media (max-width: 576px) {
  .service-provider-container {
    padding: 15px;
  }
  
  .products-grid {
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }
  
  .product-image-container {
    height: 150px;
  }
  
  .provider-details h1 {
    font-size: 24px;
  }
}
</style>